import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { XCircle, Loader2 } from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';

const ACH_REASONS = [
  { v: 'returned',       label: 'Returned' },
  { v: 'closed_account', label: 'Closed Account' },
  { v: 'invalid_routing',label: 'Invalid Routing' },
  { v: 'rejected',       label: 'Rejected' },
  { v: 'other',          label: 'Other' },
];
const CHECK_REASONS = [
  { v: 'lost',         label: 'Lost' },
  { v: 'stop_payment', label: 'Stop Payment' },
  { v: 'returned',     label: 'Returned' },
  { v: 'voided',       label: 'Voided' },
  { v: 'other',        label: 'Other' },
];

export default function MarkFailedDialog({ open, onClose, batchId, line, onFailed }) {
  const method = (line?.actual_payment_method || line?.payment_method || 'ach').toLowerCase();
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const reasons = method === 'check' ? CHECK_REASONS : ACH_REASONS;

  const submit = async () => {
    if (!reason) { toast.error('Select a reason'); return; }
    setLoading(true);
    try {
      const { data } = await accountingAPI.markBatchLineFailed(batchId, line.batch_line_id, {
        reason_code: reason, notes: notes.trim() || undefined,
      });
      onFailed?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark payment failed');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="mark-failed-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" /> Mark Payment Failed
          </DialogTitle>
          <DialogDescription>
            Recipient: <b>{line?.recipient_name}</b> · Amount: ${line?.net_payable?.toLocaleString(undefined, { minimumFractionDigits: 2 })}<br />
            This creates a <b>PAYMENT_REVERSAL</b> ledger entry (append-only) and restores the statement's remaining payable balance.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Failure Reason ({method.toUpperCase()})</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="failed-reason-select">
                <SelectValue placeholder="Choose a reason…" />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (<SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="failed-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={loading || !reason} data-testid="confirm-failed-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            Mark Failed + Reverse
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
